<script>
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'CreateUnavailabilityModal',
  components: { BaseModal },
  props: {
    unavailability: {
      type: Object,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      unavailable_date: '',
      start_time: '',
      end_time: '',
      reason: '',
      isLoading: false,
      errors: {
        unavailable_date: '',
        reason: '',
      },
    };
  },
  computed: {
    isEditMode() {
      return this.isEdit || !!this.unavailability;
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Unavailability' : 'Add Unavailability';
    },
    confirmButtonText() {
      return this.isEditMode ? 'Update' : 'Create';
    },
    staffId() {
      return this.$route.params.staffId;
    },
  },
  watch: {
    unavailability: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.populateForm(newVal);
      },
    },
  },
  methods: {
    populateForm(data) {
      this.unavailable_date = data.unavailable_date || '';
      this.start_time = data.start_time || '';
      this.end_time = data.end_time || '';
      this.reason = data.reason || '';
    },
    clearErrors() {
      this.errors = { unavailable_date: '', reason: '' };
    },
    validateForm() {
      this.clearErrors();
      let isValid = true;

      if (!this.unavailable_date) {
        this.errors.unavailable_date = 'Date is required';
        isValid = false;
      }

      if (!this.reason.trim()) {
        this.errors.reason = 'Reason is required';
        isValid = false;
      }

      return isValid;
    },
    async handleConfirm() {
      if (!this.validateForm()) return;
      this.isLoading = true;

      const payload = {
        unavailable_date: this.unavailable_date,
        reason: this.reason.trim(),
        start_time: this.start_time || null,
        end_time: this.end_time || null,
      };

      try {
        if (!this.isEditMode) {
          await this.$store.dispatch('staffV2/createStaffUnavailability', {
            staffId: this.staffId,
            payload,
          });
          useAlert('Unavailability created successfully');
        } else {
          await this.$store.dispatch('staffV2/updateStaffUnavailability', {
            staffId: this.staffId,
            unavailabilityId: this.unavailability.id,
            payload,
          });
          useAlert('Unavailability updated successfully');
        }

        await this.$store.dispatch('staffV2/fetchStaffUnavailability', { staffId: this.staffId });
        this.$emit('saved');
        this.$emit('close');
      } catch (err) {
        console.error('Error saving unavailability:', err);
        useAlert(`Error: ${err}`);
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.unavailable_date = '';
      this.start_time = '';
      this.end_time = '';
      this.reason = '';
      this.clearErrors();
    },
  },
  mounted() {
    if (!this.isEditMode) this.resetForm();
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{ modalTitle }}
    </template>

    <form @submit.prevent="handleConfirm" class="space-y-6">
      <!-- Date -->
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">
          Date<span class="text-red-500">*</span>
        </label>
        <input
          type="date"
          v-model="unavailable_date"
          :class="['w-full rounded-md border p-2.5 text-sm', errors.unavailable_date ? 'border-red-500' : 'border-slate-300']"
        />
        <p v-if="errors.unavailable_date" class="text-xs text-red-500 mt-1">{{ errors.unavailable_date }}</p>
      </div>

      <!-- Start Time -->
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">Start Time (optional)</label>
        <input
          type="time"
          v-model="start_time"
          class="w-full rounded-md border p-2.5 text-sm border-slate-300"
        />
      </div>

      <!-- End Time -->
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">End Time (optional)</label>
        <input
          type="time"
          v-model="end_time"
          class="w-full rounded-md border p-2.5 text-sm border-slate-300"
        />
      </div>

      <!-- Reason -->
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">
          Reason<span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          v-model="reason"
          placeholder="Enter reason"
          :class="['w-full rounded-md border p-2.5 text-sm', errors.reason ? 'border-red-500' : 'border-slate-300']"
        />
        <p v-if="errors.reason" class="text-xs text-red-500 mt-1">{{ errors.reason }}</p>
      </div>
    </form>

    <template #footer>
      <div class="flex justify-end gap-3 mt-4">
        <woot-button color-scheme="secondary" @click="$emit('close')" :disabled="isLoading">Cancel</woot-button>
        <woot-button @click="handleConfirm" :disabled="isLoading" :loading="isLoading">
          {{ confirmButtonText }}
        </woot-button>
      </div>
    </template>
  </BaseModal>
</template>
